/**
 * Food kcal vs resting BMR — reference only (no % coaching targets).
 */
/**
 * Compare daily food intake to resting BMR.
 * `at` when within ±1 kcal of BMR (integer rounding).
 */
export function compareIntakeToRestingBmr(mealCaloriesTotal, restingBmr) {
    if (mealCaloriesTotal == null ||
        !Number.isFinite(mealCaloriesTotal) ||
        restingBmr == null ||
        !Number.isFinite(restingBmr) ||
        restingBmr <= 0) {
        return { delta: null, underOver: null };
    }
    const intake = Math.round(mealCaloriesTotal);
    const bmr = Math.round(restingBmr);
    const delta = intake - bmr;
    if (delta < -1) {
        return { delta, underOver: 'under' };
    }
    if (delta > 1) {
        return { delta, underOver: 'over' };
    }
    return { delta: 0, underOver: 'at' };
}
/**
 * Percent of resting BMR: (intake − BMR) / BMR × 100, rounded to 1 decimal.
 * Reuses under/over from {@link compareIntakeToRestingBmr}.
 */
export function percentUnderOverBmr(mealCaloriesTotal, restingBmr) {
    const { delta, underOver } = compareIntakeToRestingBmr(mealCaloriesTotal, restingBmr);
    if (delta == null ||
        restingBmr == null ||
        !Number.isFinite(restingBmr) ||
        restingBmr <= 0) {
        return { pct: null, underOver: null };
    }
    const bmr = Math.round(restingBmr);
    const pct = Math.round((delta / bmr) * 1000) / 10;
    return { pct, underOver };
}
/**
 * Approx total daily burn (TDEE v1): resting BMR + logged exercise burn.
 * Returns null when BMR or burn is missing (omit line; do not pretend sedentary TDEE).
 */
export function approxTotalBurnKcal(restingBmr, exerciseBurnKcal) {
    if (restingBmr == null ||
        !Number.isFinite(restingBmr) ||
        restingBmr <= 0 ||
        exerciseBurnKcal == null ||
        !Number.isFinite(exerciseBurnKcal) ||
        exerciseBurnKcal < 0) {
        return null;
    }
    return Math.round(restingBmr) + Math.round(exerciseBurnKcal);
}
/** Sum non-null estimates; partial when any logged meal lacks an estimate. */
export function buildCalorieSummary(estimates) {
    let mealCaloriesTotal = 0;
    let mealsWithEstimate = 0;
    let mealsMissingEstimate = 0;
    for (const value of estimates) {
        if (value == null || !Number.isFinite(value)) {
            mealsMissingEstimate += 1;
            continue;
        }
        mealCaloriesTotal += Math.round(Number(value));
        mealsWithEstimate += 1;
    }
    return {
        mealCaloriesTotal,
        mealCaloriesPartial: mealsMissingEstimate > 0 && mealsWithEstimate > 0,
        mealsMissingEstimate,
        mealsWithEstimate,
    };
}
